import { useCallback, useEffect, useRef, useState } from "react";
import { OTP_RESEND_COUNTDOWN_SECONDS } from '@/lib/otp';

/**
 * Cooldown de reenvío de OTP refresh-proof (paridad RBAC / DEC-RBAC-056).
 *
 * Guarda el timestamp de envío (`sentAt`, epoch ms) en `sessionStorage` bajo una clave namespaced
 * por `flowKey` y computa el restante como `cooldown − (now − sentAt)`. Como la fuente de verdad es
 * el timestamp (no un contador en memoria), el countdown SOBREVIVE un refresh y resiste la pausa del
 * tab. Cada flujo usa su propia `flowKey` → cooldowns aislados ('login', 'forgot').
 */
export interface OtpCooldown {
  /** Segundos restantes hasta poder reenviar (0 si ya se puede). */
  secondsLeft: number;
  /** `true` cuando el cooldown terminó (o nunca arrancó). */
  canResend: boolean;
  /** Marca "acabo de enviar un OTP ahora" → reinicia el cooldown (persistido). */
  markSent: () => void;
  /** Olvida el cooldown (limpia el storage). Para "cancelar"/reset del flujo. */
  clear: () => void;
}

const STORAGE_PREFIX = "fiado.otpCooldown.";

/** flowKey del cooldown del login. */
export const LOGIN_OTP_FLOW_KEY = "login";
/** flowKey del cooldown de recuperación de contraseña. */
export const FORGOT_OTP_FLOW_KEY = "forgot";

function storageKey(flowKey: string): string {
  return `${STORAGE_PREFIX}${flowKey}`;
}

/** Borra el cooldown persistido de un flujo (fuera de un componente). Best-effort. */
export function clearOtpCooldown(flowKey: string): void {
  try {
    sessionStorage.removeItem(storageKey(flowKey));
  } catch {
    /* best-effort: sessionStorage inaccesible */
  }
}

function readSentAt(flowKey: string): number | null {
  try {
    const raw = sessionStorage.getItem(storageKey(flowKey));
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function computeSecondsLeft(sentAt: number | null, cooldownSeconds: number, now: number): number {
  if (sentAt === null) return 0;
  const elapsedMs = now - sentAt;
  const remainingSec = Math.ceil((cooldownSeconds * 1000 - elapsedMs) / 1000);
  return remainingSec > 0 ? remainingSec : 0;
}

export function useOtpCooldown(
  flowKey: string,
  cooldownSeconds: number = OTP_RESEND_COUNTDOWN_SECONDS,
): OtpCooldown {
  const [secondsLeft, setSecondsLeft] = useState<number>(() =>
    computeSecondsLeft(readSentAt(flowKey), cooldownSeconds, Date.now()),
  );

  const prevFlowKey = useRef(flowKey);
  useEffect(() => {
    if (prevFlowKey.current === flowKey) return;
    prevFlowKey.current = flowKey;
    queueMicrotask(() => {
      setSecondsLeft(computeSecondsLeft(readSentAt(flowKey), cooldownSeconds, Date.now()));
    });
  }, [flowKey, cooldownSeconds]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => {
      setSecondsLeft(computeSecondsLeft(readSentAt(flowKey), cooldownSeconds, Date.now()));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [secondsLeft, flowKey, cooldownSeconds]);

  const markSent = useCallback(() => {
    const now = Date.now();
    try {
      sessionStorage.setItem(storageKey(flowKey), String(now));
    } catch {
      /* best-effort */
    }
    setSecondsLeft(computeSecondsLeft(now, cooldownSeconds, now));
  }, [flowKey, cooldownSeconds]);

  const clear = useCallback(() => {
    clearOtpCooldown(flowKey);
    setSecondsLeft(0);
  }, [flowKey]);

  return { secondsLeft, canResend: secondsLeft === 0, markSent, clear };
}
